import {
	TextField,
	PrimaryButton,
	Spinner,
	mergeStyleSets,
	Checkbox,
} from '@fluentui/react';
import ReusableDialog, { ReusableDialogState } from '../../controls/ReusableDialog';
import { makeAutoObservable } from 'mobx';
import API, { IUserConfigPackage } from '../../../API';
import { observer } from 'mobx-react';
import SearchSitesAndTeams, { ISiteOrTeam, SearchSitesAndTeamsState } from '../../controls/SearchSitesAndTeams';
import { InfoTooltipLabel } from '../../controls/InfoTooltipLabel';

export class AddTemplateState {
	public userConfig: IUserConfigPackage;
	public dialogState: ReusableDialogState;
	public isAddingTemplate = false;
	public onSuccess: () => void;

	public searchBoxState:SearchSitesAndTeamsState;
	public selectedSiteOrTeam?: ISiteOrTeam;

	public includeFiles:boolean = false;
	public setIncludeFiles(v:boolean) {
		this.includeFiles = v;
	}

	public includeListItems:boolean = false;
	public setIncludeListItems(v:boolean) {
		this.includeListItems = v;
	}

	public name:string = '';
	public setName(v:string) {
		this.name = v;
	}
	
	public sourceUrl:string = '';
	public setSourceUrl(v:string) {
		this.sourceUrl = v;
	}
	
	public teamId:string = '';
	public setTeamId(v:string) {
		this.teamId = v;
	}

	public listNames:string[] = [];
	public setListNames(v:string[]) {
		this.listNames = v;
	}

	constructor(userConfig: IUserConfigPackage, onSuccess: () => void) {
		this.userConfig = userConfig;
		this.dialogState = new ReusableDialogState('Add new template', 350);
		this.searchBoxState = new SearchSitesAndTeamsState(userConfig, this.selectSiteOrTeam.bind(this));
		this.onSuccess = onSuccess;
		makeAutoObservable(this);
	}

	private selectSiteOrTeam(siteOrTeam?:ISiteOrTeam) {
		this.selectedSiteOrTeam = siteOrTeam;
		if(siteOrTeam) {
			this.teamId = siteOrTeam.id || '';
			this.sourceUrl = siteOrTeam.id || siteOrTeam.webUrl;
			this.name = siteOrTeam.displayName;
		}
	}

	public async addNewTemplate(): Promise<void> {
		this.isAddingTemplate = true;

		try {
			let token = await this.userConfig.getToken();
			let api = new API(token);
			await api.createTemplate(this.name, this.sourceUrl, this.includeFiles, this.includeListItems, this.listNames);
			this.dialogState.onDismiss();
			this.onSuccess();
		} catch {
			// We don't want to handle the error at the present moment
		} finally {
			this.isAddingTemplate = false;
		}
	}
}

const contentStyles = mergeStyleSets({
	container: {
		marginLeft: '20px',
		marginRight: '20px',
		marginBottom: '20px',
	},
	input: {
		marginBottom: '5px',
	},
	button: {
		marginTop: '15px',
		marginLeft: 'auto',
	},
	spinner: {
		marginLeft: '5px',
	},
});

function AddTemplateDialog({ state }: { state: AddTemplateState }) {

	const getNewTemplateNameError = (value: string) => {
		if (!value) {
			return 'Please enter a name.';
		}
	};

	const getNewTemplateSourceUrlError = (value: string) => {
		if (!value) {
			return 'Please enter a valid source URL.';
		}
	};

	return (
		<ReusableDialog state={state.dialogState}>
			<div className={contentStyles.container}>

				<InfoTooltipLabel label='Template content source' title='Enter a site or team name, or a site or library URL.'>
					<SearchSitesAndTeams state={state.searchBoxState} />	
				</InfoTooltipLabel>

				{state.selectedSiteOrTeam && <>

					<TextField
						label="Template name"
						value={state.name}
						className={contentStyles.input}
						onChange={(ev, val) => state.setName(val ?? '')}
						onGetErrorMessage={getNewTemplateNameError}
					/>

					{ !state.teamId && <>
						<InfoTooltipLabel title='Includes all files/documents in the template. The template will be exported as a .pnp file instead of an .xml file.'
										label='Include SharePoint files' horizontal={true}>
							<Checkbox
								checked={state.includeFiles}
								onChange={(e, checked) => { state.setIncludeFiles(checked?.valueOf() ?? false); } }			
							/>
						</InfoTooltipLabel>

						<InfoTooltipLabel title='Includes all list items in the template.'
										label='Include SharePoint List Items' horizontal={true}>
							<Checkbox
								checked={state.includeListItems}
								onChange={(e, checked) => { state.setIncludeListItems(checked?.valueOf() ?? false); } }
							/>
						</InfoTooltipLabel>
					</> }
				</>}

				<div style={{ display: 'flex' }}>
					<PrimaryButton
						disabled={
							!!getNewTemplateSourceUrlError(state.sourceUrl) ||
							!!getNewTemplateNameError(state.name) ||
							state.isAddingTemplate
						}
						className={contentStyles.button}
						onClick={() => {
							state.addNewTemplate();
						}}
					>
						Submit{' '}
						{state.isAddingTemplate ? (
							<Spinner className={contentStyles.spinner} />
						) : (
							<></>
						)}
					</PrimaryButton>
				</div>
			</div>
		</ReusableDialog>
	);
}

export default observer(AddTemplateDialog);
